/**
 * draftingSlice - RTK slice for the top-level Drafting page (AB#1573).
 *
 * Manages drafts AND strategies under one state tree. The backend
 * separates them into /drafting/drafts and /drafting/insights, but the
 * frontend treats them as two collections of the same shape, with a
 * `kind: 'draft' | 'strategy'` discriminator on each row.
 *
 * Async thunks call the new BE surface (AB#1572):
 *   GET    /drafting/drafts | /drafting/insights              → list
 *   GET    /drafting/drafts/<uuid> | /drafting/insights/<uuid> → fetch one
 *   POST   /drafting/drafts | /drafting/insights              → create
 *   PATCH  /drafting/drafts/<uuid> | …                         → metadata
 *   DELETE /drafting/drafts/<uuid> | …                         → soft delete
 *   POST   /drafting/drafts/<uuid>/generate | …                → AI generate
 *   POST   /drafting/drafts/<uuid>/attachments | …             → attach
 *   DELETE /drafting/drafts/<uuid>/attachments/<id> | …        → detach
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apis from '../../utils/apis';
import { readErrorMessage } from '../../utils/errorMessage';

export type DraftingKind = 'draft' | 'strategy';

// Backend stores 'insight' but the UI calls it 'strategy'. Translate at
// the API boundary; the rest of the app uses 'strategy'.
const kindToPath = (k: DraftingKind): 'drafts' | 'insights' =>
  k === 'draft' ? 'drafts' : 'insights';

export interface DraftingAttachment {
  id: number;
  entity_type: 'matter' | 'project';
  entity_id: number;
  created_at: string | null;
  created_by_user_id: number | null;
}

export interface DraftingItem {
  uuid: string;
  kind: DraftingKind;
  document_type_id: string;
  name: string | null;
  category: string | null;
  description: string | null;
  status: 'not_generated' | 'processing' | 'queued' | 'completed' | 'failed';
  word_count: number | null;
  version_count: number;
  matter_uuid: string | null;
  matter_id: number | null;
  /** Parent matter title, surfaced by the backend so the UI can render a
   * clickable link to /matters/<matter_uuid> without a second fetch. */
  matter_title?: string | null;
  /** Parent matter jurisdiction (e.g. 'ES') - gates the "cases" toggle in the
   * customize panel's Matter context tab. */
  matter_jurisdiction?: string | null;
  finalized: boolean;
  finalized_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  correlation_id: string | null;
  /** Live PubSub group surfaced by the backend when status is processing/queued.
   * Used by useDraftingPubSub to rejoin on refresh without guessing the name. */
  pubsub?: { hub: string; group: string } | null;
  attachments?: DraftingAttachment[];
  /**
   * Who this document is shared with - the same shape `/tables/:uuid` returns,
   * so `shared/access/accessModel.normalise` reads it without new keys.
   *
   * Only present once the backend collaborator routes ship; the UI that reads
   * it is gated behind `DRAFTING_SHARING_ENABLED`.
   */
  shared_with?: any[];
}

export interface DraftingListFilters {
  matterUuid?: string | null;
  projectId?: number | null;
  unattached?: boolean;
  status?: string | null;
  limit?: number | null;
}

export interface DraftingType {
  /** Stable id used as document_type_id in the create payload. */
  document_type_id: string;
  /** Human-friendly label for the picker. */
  name: string;
  category?: string;
  description?: string;
}

interface DraftingState {
  drafts: DraftingItem[];
  strategies: DraftingItem[];
  currentItem: DraftingItem | null;
  /** Markdown text for standalone drafts. Null until loaded or between items. */
  currentContent: string | null;
  currentEditorJson: unknown | null;
  /** DOCX binary for matter-mode drafts, assembled at stream end. */
  currentContentBlob: Blob | null;
  /** Content-loading state machine. */
  streamingState: 'idle' | 'streaming' | 'completed' | 'error';
  /** Running byte count for the progress chip. */
  contentBytesReceived: number;
  contentError: string | null;
  /** Document-type catalogue per kind. Cached after first fetch. */
  types: { draft: DraftingType[]; strategy: DraftingType[] };
  typesLoading: { draft: boolean; strategy: boolean };
  /** Per-kind error from fetchDraftingTypes - null when clean or loading. */
  typesError: { draft: string | null; strategy: string | null };
  /** UUIDs of items that have PubSub generation in flight. Cleared when a
   * terminal event (completed | failed) arrives via applyPubSubEvent. */
  generatingIds: string[];
  /** Latest human-readable step message from PubSub for the currentItem. */
  processingMessage: string | null;
  /** Latest progress percent (0-100) from PubSub for the currentItem. */
  processingProgress: number | null;
  /** Richer error for the most recent fetchDraftingItem failure - includes HTTP
   * status and server detail so the detail view can distinguish 404 from 500. */
  itemError: { message: string; status?: number; detail?: string } | null;
  listLoading: boolean;
  /** Per-kind, because the merged list is fed by two independent requests. */
  listLoadingByKind: Record<DraftingKind, boolean>;
  itemLoading: boolean;
  mutating: boolean;
  error: string | null;
  filters: DraftingListFilters;
}

const initialState: DraftingState = {
  drafts: [],
  strategies: [],
  currentItem: null,
  currentContent: null,
  currentEditorJson: null,
  currentContentBlob: null,
  streamingState: 'idle' as const,
  contentBytesReceived: 0,
  contentError: null,
  generatingIds: [],
  processingMessage: null,
  processingProgress: null,
  types: { draft: [], strategy: [] },
  typesLoading: { draft: false, strategy: false },
  typesError: { draft: null, strategy: null },
  itemError: null,
  listLoading: false,
  listLoadingByKind: { draft: false, strategy: false },
  itemLoading: false,
  mutating: false,
  error: null,
  filters: {},
};

// ─── Helper: which collection to mutate in state ────────────────────────
const bucketFor = (kind: DraftingKind) =>
  kind === 'draft' ? 'drafts' as const : 'strategies' as const;

/** The merged list is loading while EITHER kind is still in flight. */
const anyKindLoading = (byKind: Record<DraftingKind, boolean>) =>
  byKind.draft || byKind.strategy;

// ─── Async thunks ────────────────────────────────────────────────────────

export const fetchDraftingList = createAsyncThunk<
  { kind: DraftingKind; items: DraftingItem[] },
  { kind: DraftingKind; filters?: DraftingListFilters },
  { rejectValue: string }
>('drafting/fetchList', async ({ kind, filters = {} }, { rejectWithValue }) => {
  try {
    const params: Record<string, string> = {};
    if (filters.matterUuid) params.matter_uuid = filters.matterUuid;
    if (filters.projectId != null) params.project_id = String(filters.projectId);
    if (filters.unattached) params.unattached = 'true';
    if (filters.status) params.status = filters.status;
    if (filters.limit) params.limit = String(filters.limit);

    const res = await apis.get(`/drafting/${kindToPath(kind)}`, { params });
    return { kind, items: res.data?.data ?? [] };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load list'));
  }
});

export interface DraftingItemError {
  message: string;
  status?: number;
  detail?: string;
}

export const fetchDraftingItem = createAsyncThunk<
  DraftingItem,
  { kind: DraftingKind; uuid: string },
  { rejectValue: DraftingItemError }
>('drafting/fetchItem', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}`);
    const item = res.data?.data as DraftingItem | undefined;
    /*
      A 200 that carries no item is a MISS, not a hit.

      The detail page probes `draft` first and falls back to `strategy` on
      rejection, so a miss that fulfils instead of rejecting does two bad
      things: the fallback never runs, and the reducer is handed `undefined`
      to read a uuid off - which is the "Cannot read properties of undefined
      (reading 'uuid')" crash on opening a strategy by URL, where there is no
      cached kind to skip the probe with.
    */
    if (!item?.uuid) {
      return rejectWithValue({
        message: 'Document not found',
        status: res.status,
      });
    }
    return item;
  } catch (e: any) {
    return rejectWithValue({
      message: readErrorMessage(e, 'Failed to load document'),
      status: e?.response?.status,
      detail: e?.response?.data?.detail,
    });
  }
});

/**
 * Fetch generated content for a standalone draft / strategy.
 *
 * Returns the raw markdown the AI engine wrote to blob. The detail view
 * passes this to CollaborativeDocumentEditor as initialHtml (Tiptap
 * accepts markdown-ish HTML; a proper markdown→HTML renderer step is a
 * micro-follow-up but the raw markdown is human-readable for v1).
 *
 * Returns null + sets contentError when the BE returns 404 (content
 * not yet generated) or 501 (matter-mode draft - caller should read
 * via the legacy route).
 */
export const fetchDraftingContent = createAsyncThunk<
  { content: string; status: string },
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchContent', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/content`);
    return {
      content: res.data?.data?.content ?? '',
      status: res.data?.data?.status ?? 'completed',
    };
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 404) {
      return rejectWithValue('Content not yet generated');
    }
    if (status === 501) {
      return rejectWithValue('Content for matter-mode documents is served by the legacy endpoint');
    }
    return rejectWithValue(readErrorMessage(e, 'Failed to load content'));
  }
});

export const saveDraftingContent = createAsyncThunk<
  { content: string; wordCount: number | null; editorJson: unknown | null },
  { kind: DraftingKind; uuid: string; content: string; contentFormat?: 'html' | 'markdown'; saveKind?: 'auto' | 'manual'; editorJson?: unknown },
  { rejectValue: string }
>('drafting/saveContent', async ({ kind, uuid, content, contentFormat = 'html', saveKind = 'manual', editorJson }, { rejectWithValue }) => {
  try {
    const res = await apis.post(`/drafting/${kindToPath(kind)}/${uuid}/save`, {
      content,
      content_format: contentFormat,
      save_kind: saveKind,
      // Lossless editor doc - stored as a sidecar so a reload can restore exact
      // formatting (tables/nested lists) that the markdown round-trip flattens.
      editor_json: editorJson ?? undefined,
    });
    // Backend stores markdown and echoes the canonical version back so the
    // read-only view can refresh without another round-trip. We also echo the
    // just-saved editorJson so the store's canonical JSON stays in sync with the
    // new markdown - otherwise a View→Edit remount would re-seed the editor from
    // a STALE load-time sidecar and appear to revert the edit.
    return {
      content: res.data?.content ?? '',
      wordCount: res.data?.word_count ?? null,
      editorJson: editorJson ?? null,
    };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to save changes'));
  }
});

export const fetchDraftingEditorJson = createAsyncThunk<
  { editorJson: unknown | null },
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchEditorJson', async ({ kind, uuid }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/editor-json`);
    return { editorJson: res.data?.editor_json ?? null };
  } catch {
    // Non-fatal - the editor falls back to markdown when there's no fresh sidecar.
    return { editorJson: null };
  }
});

export interface MatterContextOptions {
  description?: boolean;
  laws?: boolean;
  cases?: boolean;
  source_documents?: boolean;
  questions?: boolean;
  notes?: boolean;
  tasks?: boolean;
  client_info?: boolean;
  previous_version?: boolean;
  reference_documents?: boolean;
}

interface CreatePayload {
  kind: DraftingKind;
  document_type_id: string;
  name?: string;
  description?: string;
  /** Bind this draft to a matter at creation. The backend resolves
   * matter_uuid → matter.id and sets both the legacy FK and an attachment row. */
  matter_uuid?: string;
  attachments?: Array<{ entity_type: 'matter' | 'project'; entity_id: number }>;
  /** Trigger generation immediately after create */
  autoGenerate?: boolean;
  structure_template_id?: string;
  /** Custom-prompt fields forwarded into the generate queue payload. */
  instructions?: string;
  tone?: string;
  focus_areas?: string[];
  additional_context?: string;
  /** Per-section toggles that gate matter-context loading on the AI side.
   * Only meaningful when matter_uuid is set. */
  matter_context_options?: MatterContextOptions;
}

export const createDrafting = createAsyncThunk<
  DraftingItem,
  CreatePayload,
  { rejectValue: string }
>('drafting/create', async (payload, { rejectWithValue, dispatch }) => {
  try {
    const {
      kind, autoGenerate, structure_template_id,
      instructions, tone, focus_areas, additional_context,
      matter_context_options,
      ...body
    } = payload;
    const res = await apis.post(`/drafting/${kindToPath(kind)}`, body);
    const created = res.data?.data as DraftingItem;
    if (autoGenerate && created?.uuid) {
      // Fire generate but don't block the create response - the polling /
      // PubSub event flow will surface progress in the detail view.
      void dispatch(generateDrafting({
        kind,
        uuid: created.uuid,
        structure_template_id,
        instructions,
        tone,
        focus_areas,
        additional_context,
        matter_context_options,
      }));
    }
    return created;
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to create'));
  }
});

export const patchDrafting = createAsyncThunk<
  DraftingItem,
  { kind: DraftingKind; uuid: string; fields: Partial<Pick<DraftingItem, 'name' | 'description' | 'category'>> },
  { rejectValue: string }
>('drafting/patch', async ({ kind, uuid, fields }, { rejectWithValue }) => {
  try {
    const res = await apis.patch(`/drafting/${kindToPath(kind)}/${uuid}`, fields);
    return res.data?.data as DraftingItem;
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to update'));
  }
});

export const deleteDrafting = createAsyncThunk<
  { kind: DraftingKind; uuid: string },
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/delete', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    await apis.delete(`/drafting/${kindToPath(kind)}/${uuid}`);
    return { kind, uuid };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to delete'));
  }
});

/**
 * Download the document as a Word (.docx) file. Hits the stream endpoint with
 * `format: 'docx', disposition: 'attachment'` and triggers a browser download.
 * The backend serves the branded DOCX deliverable the AI engine wrote alongside
 * the editable markdown source.
 */
export const downloadDrafting = createAsyncThunk<
  void,
  { kind: DraftingKind; uuid: string; filename?: string; format?: 'docx' | 'pdf' },
  { rejectValue: string }
>('drafting/download', async ({ kind, uuid, filename, format = 'docx' }, { rejectWithValue }) => {
  try {
    // arraybuffer (not blob) so we can inspect the bytes before saving - a
    // corrupt/mislabelled response must never be written to disk. The backend
    // applies the branding template ATTACHED to the draft automatically.
    const res = await apis.post(
      `/drafting/${kindToPath(kind)}/${uuid}/stream`,
      { format, disposition: 'attachment' },
      { responseType: 'arraybuffer' },
    );
    const buf: ArrayBuffer = res.data;
    const bytes = new Uint8Array(buf);

    // Validate the magic bytes for the requested format before saving - a
    // mislabelled response (markdown, EA1 blob, JSON error) written as .docx/.pdf
    // is what makes the app report "unreadable content". Fail loudly instead.
    const isZip = bytes.length > 3 && bytes[0] === 0x50 && bytes[1] === 0x4b;          // PK (docx)
    const isPdf = bytes.length > 4 && bytes[0] === 0x25 && bytes[1] === 0x50
      && bytes[2] === 0x44 && bytes[3] === 0x46;                                        // %PDF
    const ok = format === 'pdf' ? isPdf : isZip;
    if (!ok) {
      let detail = '';
      try { detail = new TextDecoder().decode(bytes.slice(0, 120)); } catch { /* ignore */ }
      if (detail.startsWith('EA1')) {
        return rejectWithValue('Document came back still-encrypted - the backend did not decrypt it.');
      }
      return rejectWithValue(
        `No ${format.toUpperCase()} is available for this draft yet. If it was generated ` +
        'before export was enabled, regenerate it and try again.',
      );
    }

    const mime = format === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const blob = new Blob([buf], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(filename || 'document').replace(/[^\w.-]+/g, '_')}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e: any) {
    // arraybuffer error bodies need decoding to read the server message.
    let msg = e?.message ?? 'Failed to download';
    try {
      if (e?.response?.data instanceof ArrayBuffer) {
        const parsed = JSON.parse(new TextDecoder().decode(e.response.data));
        msg = parsed?.error ?? msg;
      }
    } catch { /* ignore */ }
    return rejectWithValue(msg);
  }
});

export const generateDrafting = createAsyncThunk<
  { kind: DraftingKind; uuid: string; pubsub?: { group: string }; result: any; unchanged: boolean },
  {
    kind: DraftingKind;
    uuid: string;
    structure_template_id?: string;
    /** Branding template to apply to the finalized document (deterministic). */
    branding_template_id?: string;
    instructions?: string;
    tone?: string;
    focus_areas?: string[];
    additional_context?: string;
    matter_context_options?: MatterContextOptions;
    /** Force a regenerate even when the context is unchanged (bypasses the
     * backend's no-change short-circuit - used after the warn-and-confirm). */
    force?: boolean;
  },
  { rejectValue: string }
>('drafting/generate', async (
  { kind, uuid, structure_template_id, branding_template_id, instructions, tone, focus_areas, additional_context, matter_context_options, force },
  { rejectWithValue },
) => {
  try {
    const res = await apis.post(`/drafting/${kindToPath(kind)}/${uuid}/generate`, {
      structure_template_id,
      branding_template_id,
      instructions,
      tone,
      focus_areas,
      additional_context,
      matter_context_options,
      force,
    });
    const { pubsub, ...rest } = res.data ?? {};
    // Backend returns status:'unchanged' (no enqueue) when the context matches
    // the last generation and force wasn't set - the caller warns + retries.
    return { kind, uuid, pubsub, result: rest, unchanged: rest?.status === 'unchanged' };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to start generation'));
  }
});

export const addDraftingAttachment = createAsyncThunk<
  { kind: DraftingKind; uuid: string; attachment: DraftingAttachment },
  { kind: DraftingKind; uuid: string; entity_type: 'matter' | 'project'; entity_id: number },
  { rejectValue: string }
>('drafting/addAttachment', async ({ kind, uuid, entity_type, entity_id }, { rejectWithValue, dispatch }) => {
  try {
    const res = await apis.post(`/drafting/${kindToPath(kind)}/${uuid}/attachments`, {
      entity_type,
      entity_id,
    });
    // Re-fetch the item so currentItem.matter_uuid (resolved from the new
    // attachment on the backend) is populated - that's what drives the
    // customize panel's Matter context tab + matter-aware generation.
    if (entity_type === 'matter') {
      void dispatch(fetchDraftingItem({ kind, uuid }));
    }
    return { kind, uuid, attachment: res.data?.data as DraftingAttachment };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to attach'));
  }
});

/**
 * Fetch the document-type catalogue for the given kind.
 *
 * Hits the legacy GET /drafts/document-types or /insights/document-types
 * endpoints (the new /drafting/* surface doesn't expose types separately;
 * the legacy catalogue is the source of truth for both flows).
 *
 * Response shape: { documents: [...] } for drafts, { strategies: [...] }
 * for insights - normalized to DraftingType[] here.
 */
export const fetchDraftingTypes = createAsyncThunk<
  { kind: DraftingKind; types: DraftingType[] },
  { kind: DraftingKind },
  { rejectValue: string }
>('drafting/fetchTypes', async ({ kind }, { rejectWithValue }) => {
  try {
    const path = kind === 'draft' ? '/drafts/document-types' : '/insights/document-types';
    const res = await apis.get(path);
    const list = kind === 'draft'
      ? (res.data?.documents ?? [])
      : (res.data?.strategies ?? res.data?.documents ?? []);

    // Normalize - the legacy payload uses slightly different field names
    // across draft/insight categories. Coerce into the DraftingType shape.
    const types: DraftingType[] = (Array.isArray(list) ? list : []).map((t: any) => ({
      document_type_id: t.document_type_id || t.id || t.name || '',
      name: t.name || t.title || t.label || (t.document_type_id || ''),
      category: t.category,
      description: t.description,
    })).filter((t: DraftingType) => t.document_type_id);

    return { kind, types };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load types'));
  }
});

export const removeDraftingAttachment = createAsyncThunk<
  { kind: DraftingKind; uuid: string; attachmentId: number },
  { kind: DraftingKind; uuid: string; attachmentId: number },
  { rejectValue: string }
>('drafting/removeAttachment', async ({ kind, uuid, attachmentId }, { rejectWithValue, dispatch }) => {
  try {
    await apis.delete(`/drafting/${kindToPath(kind)}/${uuid}/attachments/${attachmentId}`);
    // Refresh so matter_uuid clears if the last matter link was removed.
    void dispatch(fetchDraftingItem({ kind, uuid }));
    return { kind, uuid, attachmentId };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to detach'));
  }
});

// ─── Customization workspace: recipe, files, version history ───────────────

/** The generation recipe persisted by the backend on each /generate, read
 * back so the customize workspace can pre-fill instead of starting blank. */
export interface DraftingGenerateOptions {
  instructions?: string;
  tone?: string;
  focus_areas?: string[];
  additional_context?: string;
  structure_template_id?: string;
  matter_context_options?: MatterContextOptions;
}

export interface DraftingFile {
  filename: string;
  size_bytes: number | null;
}

export interface DraftingVersion {
  version_id: string;
  last_modified: string | null;
  size_bytes: number | null;
  is_current: boolean;
}

/** Read the last-used generation recipe for a document. Returns {} when the
 * document has never been generated. */
export const fetchDraftingGenerateOptions = createAsyncThunk<
  DraftingGenerateOptions,
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchGenerateOptions', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/generate-options`);
    return (res.data?.options ?? {}) as DraftingGenerateOptions;
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load options'));
  }
});

/** List the reference files (context attachments) uploaded for a document. */
export const fetchDraftingFiles = createAsyncThunk<
  DraftingFile[],
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchFiles', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/files`);
    return (res.data?.attachments ?? []) as DraftingFile[];
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load files'));
  }
});

/** Upload one reference file. Matches the wizard's multipart pattern. */
export const uploadDraftingFile = createAsyncThunk<
  DraftingFile,
  { kind: DraftingKind; uuid: string; file: File },
  { rejectValue: string }
>('drafting/uploadFile', async ({ kind, uuid, file }, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await apis.post(
      `/drafting/${kindToPath(kind)}/${uuid}/files`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return {
      filename: res.data?.filename ?? file.name,
      size_bytes: res.data?.size_bytes ?? file.size,
    };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to upload file'));
  }
});

/** Delete a reference file by filename. */
export const deleteDraftingFile = createAsyncThunk<
  { filename: string },
  { kind: DraftingKind; uuid: string; filename: string },
  { rejectValue: string }
>('drafting/deleteFile', async ({ kind, uuid, filename }, { rejectWithValue }) => {
  try {
    await apis.delete(`/drafting/${kindToPath(kind)}/${uuid}/files/${encodeURIComponent(filename)}`);
    return { filename };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to delete file'));
  }
});

/** List Azure blob versions of a document's markdown source (newest first). */
export const fetchDraftingVersions = createAsyncThunk<
  { versions: DraftingVersion[]; versioningAvailable: boolean },
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchVersions', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/versions`);
    return {
      versions: (res.data?.versions ?? []) as DraftingVersion[],
      versioningAvailable: Boolean(res.data?.versioning_available),
    };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load versions'));
  }
});

/** Fetch a specific version's markdown WITHOUT mutating currentContent —
 * used by the inline version rail to preview a prior version before deciding
 * whether to restore it. */
export const fetchDraftingVersionContent = createAsyncThunk<
  { versionId: string; content: string },
  { kind: DraftingKind; uuid: string; versionId: string },
  { rejectValue: string }
>('drafting/fetchVersionContent', async ({ kind, uuid, versionId }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/content`, {
      params: { version_id: versionId },
    });
    return { versionId, content: res.data?.data?.content ?? '' };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load version'));
  }
});

/** Restore a document to a prior version. Updates currentItem's word_count /
 * updated_at via the extraReducer so the header reflects the restored state. */
export const restoreDraftingVersion = createAsyncThunk<
  { kind: DraftingKind; uuid: string; wordCount: number | null },
  { kind: DraftingKind; uuid: string; versionId: string },
  { rejectValue: string }
>('drafting/restoreVersion', async ({ kind, uuid, versionId }, { rejectWithValue }) => {
  try {
    const res = await apis.post(`/drafting/${kindToPath(kind)}/${uuid}/restore`, {
      version_id: versionId,
    });
    return { kind, uuid, wordCount: res.data?.word_count ?? null };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to restore version'));
  }
});

/* ── Collaborators ───────────────────────────────────────────────────────
 *
 * The same four calls Tables makes (`BatchTablesActions`), against the routes
 * the backend is adding for drafting. Every mutation refetches the list, so
 * the pane, the rail badge and the avatar stack all read one source.
 *
 * Nothing dispatches these while `DRAFTING_SHARING_ENABLED` is false.
 */

export const fetchDraftingCollaborators = createAsyncThunk<
  { uuid: string; collaborators: any[] },
  { kind: DraftingKind; uuid: string },
  { rejectValue: string }
>('drafting/fetchCollaborators', async ({ kind, uuid }, { rejectWithValue }) => {
  try {
    const res = await apis.get(`/drafting/${kindToPath(kind)}/${uuid}/collaborators`);
    return { uuid, collaborators: res.data?.collaborators ?? [] };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to load collaborators'));
  }
});

export const addDraftingCollaborator = createAsyncThunk<
  { kind: DraftingKind; uuid: string },
  { kind: DraftingKind; uuid: string; collaboratorIdOrUuid: string | number; access?: string },
  { rejectValue: string }
>('drafting/addCollaborator', async (
  { kind, uuid, collaboratorIdOrUuid, access = 'view' },
  { dispatch, rejectWithValue },
) => {
  try {
    // Numeric id (legacy) vs uuid from the org-people API - the same branch
    // `addTableCollaborator` makes, because the backend accepts either.
    const data: Record<string, unknown> = { access };
    if (typeof collaboratorIdOrUuid === 'number' || !Number.isNaN(Number(collaboratorIdOrUuid))) {
      data.user_id = Number(collaboratorIdOrUuid);
    } else {
      data.collaborator_uuid = collaboratorIdOrUuid;
    }
    await apis.post(`/drafting/${kindToPath(kind)}/${uuid}/collaborators`, data);
    await dispatch(fetchDraftingCollaborators({ kind, uuid }));
    return { kind, uuid };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to grant access'));
  }
});

export const updateDraftingCollaboratorAccess = createAsyncThunk<
  { kind: DraftingKind; uuid: string },
  { kind: DraftingKind; uuid: string; collaboratorUuid: string; access: string },
  { rejectValue: string }
>('drafting/updateCollaboratorAccess', async (
  { kind, uuid, collaboratorUuid, access },
  { dispatch, rejectWithValue },
) => {
  try {
    await apis.put(
      `/drafting/${kindToPath(kind)}/${uuid}/collaborators/${collaboratorUuid}`,
      { access },
    );
    await dispatch(fetchDraftingCollaborators({ kind, uuid }));
    return { kind, uuid };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to update access'));
  }
});

export const removeDraftingCollaborator = createAsyncThunk<
  { kind: DraftingKind; uuid: string },
  { kind: DraftingKind; uuid: string; collaboratorUuid: string },
  { rejectValue: string }
>('drafting/removeCollaborator', async (
  { kind, uuid, collaboratorUuid },
  { dispatch, rejectWithValue },
) => {
  try {
    await apis.delete(`/drafting/${kindToPath(kind)}/${uuid}/collaborators/${collaboratorUuid}`);
    await dispatch(fetchDraftingCollaborators({ kind, uuid }));
    return { kind, uuid };
  } catch (e: any) {
    return rejectWithValue(readErrorMessage(e, 'Failed to remove collaborator'));
  }
});

// ─── Slice ───────────────────────────────────────────────────────────────

const draftingSlice = createSlice({
  name: 'drafting',
  initialState,
  reducers: {
    setListFilters(state, action: PayloadAction<DraftingListFilters>) {
      state.filters = action.payload;
    },
    clearCurrentItem(state) {
      state.currentItem = null;
      state.currentContent = null;
      state.currentContentBlob = null;
      state.contentError = null;
      state.itemError = null;
      state.streamingState = 'idle';
      state.contentBytesReceived = 0;
      state.processingMessage = null;
      state.processingProgress = null;
    },
    clearStreamingState(state) {
      state.currentContent = null;
      state.currentContentBlob = null;
      state.contentError = null;
      state.streamingState = 'idle';
      state.contentBytesReceived = 0;
    },
    streamingStarted(state, _action: PayloadAction<{ kind: DraftingKind; uuid: string }>) {
      state.streamingState = 'streaming';
      state.contentBytesReceived = 0;
      state.currentContent = '';
      state.currentContentBlob = null;
      state.contentError = null;
    },
    streamingAppendChunk(
      state,
      action: PayloadAction<{ kind: DraftingKind; uuid: string; text?: string; blobChunk?: Blob }>
    ) {
      if (action.payload.text !== undefined) {
        state.currentContent = (state.currentContent ?? '') + action.payload.text;
        state.contentBytesReceived = state.currentContent.length;
      } else if (action.payload.blobChunk !== undefined) {
        // Reducer ignores Blob value - final Blob assembled in streamingDone.
        // Only track byte count for the progress chip.
        state.contentBytesReceived += action.payload.blobChunk.size;
      }
    },
    streamingDone(
      state,
      action: PayloadAction<{ kind: DraftingKind; uuid: string; finalBlob?: Blob }>
    ) {
      state.streamingState = 'completed';
      if (action.payload.finalBlob) {
        state.currentContentBlob = action.payload.finalBlob;
      }
    },
    streamingFailed(state, action: PayloadAction<{ kind: DraftingKind; uuid: string; error: string }>) {
      state.streamingState = 'error';
      state.contentError = action.payload.error;
    },
    clearError(state) {
      state.error = null;
    },
    /**
     * Optimistically flip a draft into the generating state - used by the
     * source-delta re-run, which enqueues on the backend but (unlike the
     * generate thunk) has no fulfilled reducer to mark it. Mirrors
     * generateDrafting.fulfilled: status → 'queued', stamp the pubsub group so
     * DraftingDetail's hydration effect joins, and add to generatingIds so the
     * progress UI shows instantly instead of waiting for the first PubSub event
     * or a manual refresh.
     */
    markGenerating(
      state,
      action: PayloadAction<{ kind: DraftingKind; uuid: string; group?: string }>
    ) {
      const { kind, uuid, group } = action.payload;
      const pubsubInfo = group ? { hub: 'ai_processing', group } : null;
      const bucket = state[bucketFor(kind)];
      const i = bucket.findIndex((d) => d.uuid === uuid);
      if (i >= 0) {
        bucket[i] = { ...bucket[i], status: 'queued', pubsub: pubsubInfo ?? bucket[i].pubsub };
      }
      if (state.currentItem?.uuid === uuid) {
        state.currentItem = {
          ...state.currentItem,
          status: 'queued',
          pubsub: pubsubInfo ?? state.currentItem.pubsub,
        };
      }
      if (!state.generatingIds.includes(uuid)) state.generatingIds.push(uuid);
    },
    applyPubSubEvent(
      state,
      action: PayloadAction<{
        correlationId: string;
        status: string;
        sub_type?: string;
        progress_percent?: number | null;
        message?: string;
      }>
    ) {
      const { correlationId, status, sub_type, progress_percent, message } = action.payload;
      const knownStatuses: DraftingItem['status'][] = [
        'not_generated', 'processing', 'queued', 'completed', 'failed',
      ];
      const mappedStatus = knownStatuses.includes(status as DraftingItem['status'])
        ? (status as DraftingItem['status'])
        : undefined;

      // Match either by item.uuid or by item.correlation_id - backend may use
      // a separate draft_id as the PubSub correlation key.
      const isCurrent =
        state.currentItem?.uuid === correlationId ||
        state.currentItem?.correlation_id === correlationId;

      if (mappedStatus) {
        // Stale / out-of-order guard: PubSub delivery is not ordered, so a late
        // non-terminal progress event ('processing'/'queued'/'not_generated')
        // can arrive AFTER the terminal 'completed' event. Never let such an
        // event downgrade an already-terminal item - that flips status back to
        // processing, which trips DraftingDetail's reset effect, wipes the
        // streamed content, and re-opens the stream (the visible flicker to
        // "No preview available yet"). A genuine re-generation flips the status
        // to 'queued' through generateDrafting.fulfilled, not here.
        const isStaleDowngrade = (prev?: DraftingItem['status']) =>
          (prev === 'completed' || prev === 'failed') &&
          mappedStatus !== 'completed' && mappedStatus !== 'failed';

        if (isCurrent && state.currentItem && !isStaleDowngrade(state.currentItem.status)) {
          state.currentItem = { ...state.currentItem, status: mappedStatus };
        }
        for (const bucket of ['drafts', 'strategies'] as const) {
          const i = state[bucket].findIndex(
            (d) => d.uuid === correlationId || d.correlation_id === correlationId,
          );
          if (i >= 0 && !isStaleDowngrade(state[bucket][i].status)) {
            state[bucket][i] = { ...state[bucket][i], status: mappedStatus };
          }
        }
      }

      const isTerminal = status === 'completed' || status === 'failed'
        || sub_type === 'draft_completed' || sub_type === 'draft_failed';

      // Progress + message only relevant for the currentItem (header badge).
      if (isCurrent) {
        if (isTerminal) {
          state.processingMessage = null;
          state.processingProgress = null;
        } else {
          if (typeof message === 'string' && message.length > 0) {
            state.processingMessage = message;
          }
          if (progress_percent !== undefined && progress_percent !== null) {
            state.processingProgress = Number(progress_percent);
          }
        }
      }

      if (isTerminal) {
        state.generatingIds = state.generatingIds.filter((id) => id !== correlationId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      /*
        ── List ────────────────────────────────────────────────

        TWO REQUESTS, ONE LIST. Drafts and strategies are separate resources
        on the API, so the page fires both and merges them. `listLoading` was
        a single boolean shared between the two, and whichever landed first
        flipped it to false - declaring the list loaded while the other kind
        was still in flight.

        That is not a cosmetic race. `/drafting/drafts` takes ~19s on this
        backend and `/drafting/insights` ~6s, so for thirteen seconds the page
        rendered "Showing 1 of 1 items" over a list holding only strategies,
        which reads exactly like documents being missing from it - and is what
        it was reported as.

        Loading is tracked PER KIND now, and the list is loading while either
        is outstanding. The count and the rows arrive together or not at all.
      */
      .addCase(fetchDraftingList.pending, (state, action) => {
        state.listLoadingByKind[action.meta.arg.kind] = true;
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchDraftingList.fulfilled, (state, action) => {
        state[bucketFor(action.payload.kind)] = action.payload.items;
        state.listLoadingByKind[action.payload.kind] = false;
        state.listLoading = anyKindLoading(state.listLoadingByKind);
      })
      .addCase(fetchDraftingList.rejected, (state, action) => {
        state.listLoadingByKind[action.meta.arg.kind] = false;
        state.listLoading = anyKindLoading(state.listLoadingByKind);
        state.error = action.payload ?? 'Failed to load list';
      })

      // ── Fetch one ───────────────────────────────────────────
      .addCase(fetchDraftingItem.pending, (state) => {
        state.itemLoading = true;
        state.error = null;
        state.itemError = null;
      })
      .addCase(fetchDraftingItem.fulfilled, (state, action) => {
        state.itemLoading = false;
        const incoming = action.payload;
        /* Belt as well as braces: the thunk rejects an empty envelope now, so
           a fulfilled action always carries an item - but this reducer is the
           one that crashed, and it should not be the thing that trusts. */
        if (!incoming?.uuid) return;
        state.itemError = null;
        const prev = state.currentItem;
        // Drop any stale content when the current item changes - the
        // stream effect will restart for the new item.
        if (prev?.uuid !== incoming.uuid) {
          state.currentContent = null;
          state.currentContentBlob = null;
          state.contentError = null;
          state.streamingState = 'idle';
          state.contentBytesReceived = 0;
        }
        // Guard against a stale refetch DOWNGRADING a live status. Generation
        // moves faster than the DB read: PubSub (or the optimistic generate)
        // may already have the item at processing/completed while the backend
        // GET still reports not_generated/queued. Without this guard the UI
        // flickers back to the empty "Click Generate" state mid-generation.
        // PubSub events (applyPubSubEvent) set status directly and remain the
        // source of truth for live transitions.
        const RANK: Record<DraftingItem['status'], number> = {
          not_generated: 0, queued: 1, processing: 2, completed: 3, failed: 3,
        };
        let status = incoming.status;
        if (prev && prev.uuid === incoming.uuid && (RANK[prev.status] ?? 0) > (RANK[incoming.status] ?? 0)) {
          status = prev.status;
        }
        state.currentItem = {
          ...incoming,
          status,
          // Keep a live pubsub group if the refetch response omitted it.
          pubsub: incoming.pubsub ?? prev?.pubsub,
        };
      })
      .addCase(fetchDraftingItem.rejected, (state, action) => {
        state.itemLoading = false;
        // Keep a string in the generic `error` field for backward compat,
        // but also populate the richer `itemError` for the detail view.
        state.error = action.payload?.message ?? 'Failed to load document';
        state.itemError = action.payload ?? null;
      })

      // ── Fetch content (JSON fallback) ───────────────────────────────
      .addCase(fetchDraftingContent.pending, (state) => {
        state.streamingState = 'streaming';
        state.contentError = null;
        // Drop any editor JSON from a previously-open document.
        state.currentEditorJson = null;
      })
      .addCase(fetchDraftingContent.fulfilled, (state, action) => {
        state.streamingState = 'completed';
        state.currentContent = action.payload.content;
      })
      .addCase(fetchDraftingContent.rejected, (state, action) => {
        state.streamingState = 'error';
        state.currentContent = null;
        state.contentError = action.payload ?? 'Failed to load content';
      })
      .addCase(saveDraftingContent.fulfilled, (state, action) => {
        // Keep the read-only View in sync with the saved edit. The editor holds
        // its own working copy while editing; this only updates what View renders
        // once the user toggles back.
        if (action.payload.content) {
          state.currentContent = action.payload.content;
        }
        // Keep the canonical JSON in lockstep with the markdown we just stored,
        // so a remount seeds the editor from the FRESH doc, not a stale sidecar.
        // (null when this save carried no editorJson - e.g. a markdown-only save.)
        state.currentEditorJson = action.payload.editorJson ?? null;
      })
      .addCase(fetchDraftingEditorJson.fulfilled, (state, action) => {
        state.currentEditorJson = action.payload.editorJson ?? null;
      })

      // ── Create ──────────────────────────────────────────────
      .addCase(createDrafting.pending, (state) => { state.mutating = true; })
      .addCase(createDrafting.fulfilled, (state, action) => {
        state.mutating = false;
        const created = action.payload;
        if (created) {
          state[bucketFor(created.kind)].unshift(created);
        }
      })
      .addCase(createDrafting.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload ?? 'Failed to create';
      })

      // ── Patch ───────────────────────────────────────────────
      .addCase(patchDrafting.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated) return;
        const bucket = state[bucketFor(updated.kind)];
        const i = bucket.findIndex((d) => d.uuid === updated.uuid);
        if (i >= 0) bucket[i] = { ...bucket[i], ...updated };
        if (state.currentItem?.uuid === updated.uuid) {
          state.currentItem = { ...state.currentItem, ...updated };
        }
      })

      // ── Delete ──────────────────────────────────────────────
      .addCase(deleteDrafting.fulfilled, (state, action) => {
        const { kind, uuid } = action.payload;
        const bucket = bucketFor(kind);
        state[bucket] = state[bucket].filter((d) => d.uuid !== uuid);
        if (state.currentItem?.uuid === uuid) state.currentItem = null;
      })

      // ── Generate (status flips to queued, UUID added to generatingIds) ──
      // Writes pubsub.group onto the item so the hydration effect in
      // DraftingDetail picks it up uniformly - whether the trigger was our
      // own handleGenerate, the Customize panel, or the Create modal.
      .addCase(generateDrafting.fulfilled, (state, action) => {
        const { kind, uuid, pubsub, unchanged } = action.payload;
        // No-change short-circuit: backend didn't enqueue, so don't flip the
        // item into a (fake) queued/generating state. The caller shows a warn.
        if (unchanged) return;
        const pubsubInfo = pubsub?.group
          ? { hub: 'ai_processing', group: pubsub.group }
          : null;
        const bucket = state[bucketFor(kind)];
        const i = bucket.findIndex((d) => d.uuid === uuid);
        if (i >= 0) {
          bucket[i] = { ...bucket[i], status: 'queued', pubsub: pubsubInfo };
        }
        if (state.currentItem?.uuid === uuid) {
          state.currentItem = {
            ...state.currentItem,
            status: 'queued',
            pubsub: pubsubInfo,
          };
        } else if (i >= 0) {
          // Freshly-created draft (wizard create → generate → navigate):
          // currentItem isn't this item yet. Seed it with the just-queued row
          // so the detail view shows 'processing' immediately instead of
          // flickering the empty "Click Generate" placeholder while the first
          // GET is in flight. The rank guard in fetchDraftingItem.fulfilled
          // then stops the DB read (still 'not_generated') from downgrading it.
          state.currentItem = bucket[i];
        }
        if (!state.generatingIds.includes(uuid)) state.generatingIds.push(uuid);
      })

      // ── Type catalogue ──────────────────────────────────────
      .addCase(fetchDraftingTypes.pending, (state, action) => {
        const kind = action.meta.arg.kind;
        state.typesLoading[kind] = true;
        state.typesError[kind] = null;
      })
      .addCase(fetchDraftingTypes.fulfilled, (state, action) => {
        const { kind, types } = action.payload;
        state.typesLoading[kind] = false;
        state.typesError[kind] = null;
        state.types[kind] = types;
      })
      .addCase(fetchDraftingTypes.rejected, (state, action) => {
        const kind = action.meta.arg.kind;
        state.typesLoading[kind] = false;
        // Don't surface as the global error - fallback to free-text input
        // in the modal. The per-kind typesError drives the warning hint.
        state.typesError[kind] = action.payload ?? 'Failed to load types';
      })

      // ── Attachments ─────────────────────────────────────────
      .addCase(addDraftingAttachment.fulfilled, (state, action) => {
        const { uuid, attachment } = action.payload;
        if (state.currentItem?.uuid === uuid) {
          state.currentItem = {
            ...state.currentItem,
            attachments: [...(state.currentItem.attachments ?? []), attachment],
          };
        }
      })
      .addCase(removeDraftingAttachment.fulfilled, (state, action) => {
        const { uuid, attachmentId } = action.payload;
        if (state.currentItem?.uuid === uuid && state.currentItem.attachments) {
          state.currentItem = {
            ...state.currentItem,
            attachments: state.currentItem.attachments.filter((a) => a.id !== attachmentId),
          };
        }
      })

      // ── Restore a prior version (blob is now current; refresh the row) ──
      .addCase(restoreDraftingVersion.fulfilled, (state, action) => {
        const { uuid, wordCount } = action.payload;
        const patch = (item: DraftingItem): DraftingItem => ({
          ...item,
          word_count: wordCount ?? item.word_count,
          updated_at: new Date().toISOString(),
        });
        if (state.currentItem?.uuid === uuid) {
          state.currentItem = patch(state.currentItem);
        }
        for (const bucket of ['drafts', 'strategies'] as const) {
          const i = state[bucket].findIndex((d) => d.uuid === uuid);
          if (i >= 0) state[bucket][i] = patch(state[bucket][i]);
        }
      })

      /* The roster lands on the item itself, under the key the detail GET will
         carry once the backend ships it - so the pane, the rail badge and the
         header stack all read `item.shared_with` and nothing else. */
      .addCase(fetchDraftingCollaborators.fulfilled, (state, action) => {
        const { uuid, collaborators } = action.payload;
        if (state.currentItem?.uuid === uuid) {
          state.currentItem = { ...state.currentItem, shared_with: collaborators };
        }
        for (const bucket of ['drafts', 'strategies'] as const) {
          const i = state[bucket].findIndex((d) => d.uuid === uuid);
          if (i >= 0) state[bucket][i] = { ...state[bucket][i], shared_with: collaborators };
        }
      });
  },
});

export const {
  setListFilters,
  clearCurrentItem,
  clearStreamingState,
  clearError,
  streamingStarted,
  streamingAppendChunk,
  streamingDone,
  streamingFailed,
  applyPubSubEvent,
  markGenerating,
} = draftingSlice.actions;
export default draftingSlice.reducer;
